"use client"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"

interface AnimatedTextCycleProps {
    words: string[]
    interval?: number
    className?: string
}

export default function AnimatedTextCycle({
    words,
    interval = 5000,
    className = "",
}: AnimatedTextCycleProps) {
    const [currentIndex, setCurrentIndex] = useState(0)
    const reducedMotion = useReducedMotion()

    useEffect(() => {
        if (reducedMotion || words.length < 2) return
        const timer = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % words.length)
        }, interval)

        return () => clearInterval(timer)
    }, [interval, reducedMotion, words.length])

    // Container animation for the whole word
    const containerVariants = {
        hidden: {
            y: -20,
        },
        visible: {
            y: 0,
            transition: {
                duration: 0.4,
            },
        },
        exit: {
            y: 20,
            transition: {
                duration: 0.3,
            },
        },
    }

    return (
        <span className="relative inline-grid overflow-hidden align-bottom">
            {/* Reserve the widest phrase so cycling never changes line wraps. */}
            {words.map((word) => (
                <span
                    key={word}
                    aria-hidden="true"
                    className={cn(
                        "invisible col-start-1 row-start-1 whitespace-nowrap font-bold",
                        className
                    )}
                >
                    {word}
                </span>
            ))}
            <AnimatePresence mode="wait" initial={false}>
                <motion.span
                    key={currentIndex}
                    className={cn(
                        "col-start-1 row-start-1 inline-block whitespace-nowrap font-bold",
                        className
                    )}
                    variants={containerVariants}
                    initial={reducedMotion ? false : "hidden"}
                    animate="visible"
                    exit="exit"
                    style={{ whiteSpace: "nowrap" }}
                >
                    {words[currentIndex]}
                </motion.span>
            </AnimatePresence>
        </span>
    )
}
